import React from 'react';
import { Card, CardMedia, Typography, Box, Chip, Button } from '@mui/material';
import {
  PurchasePackagesType,
  SubscriptionPackagesType
} from '../../types/types';

// Arena palette — mirrors the redesigned dashboard / leaderboard so the pricing
// cards read as part of the same system across Subscription + Giveaway.
const C = {
  cyan: '#38e8ff',
  gold: '#ffcf40',
  green: '#42e6a4',
  text: '#e8f4fb',
  dim: '#8ea3b3',
  dimmer: '#5c6b78',
  lineSoft: 'rgba(140, 170, 190, 0.09)',
  mono: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace'
};

interface PackageRarity {
  rarity: string;
  guaranteed: boolean;
  pullable: boolean;
}

interface PackageDetails {
  id: number;
  name: string;
  type: string;
  description: string;
  imageUrl: string;
  price: string;
  cards: number;
  tokens: number;
  entries: number;
  purchaseText?: string;
  isDisabled?: boolean;
  PackageRarities: PackageRarity[];
}

type PackageDisplayProps = {
  packageData: PackageDetails;
  handlePurchaseSelection: (
    purchasePackage: PurchasePackagesType | SubscriptionPackagesType
  ) => void;
  isTopTier?: boolean;
  discountedSubscriptionPrice?: string | null;
  startingBonusEntries?: number;
};

const rarityColor = (r: PackageRarity) =>
  r.guaranteed ? C.green : r.pullable ? C.cyan : C.dimmer;

export const PackageDisplay: React.FC<PackageDisplayProps> = ({
  packageData,
  handlePurchaseSelection,
  isTopTier = false,
  discountedSubscriptionPrice = null,
  startingBonusEntries = 0
}) => {
  const { name, imageUrl, price, PackageRarities, type } = packageData;
  const disabled = packageData.isDisabled ?? false;

  const defaultRarities = [
    { rarity: 'common', guaranteed: false, pullable: false },
    { rarity: 'rare', guaranteed: false, pullable: false },
    { rarity: 'epic', guaranteed: false, pullable: false },
    { rarity: 'legendary', guaranteed: false, pullable: false },
    { rarity: 'relic', guaranteed: false, pullable: false }
  ];

  // Merge the default rarities with database data
  const mergedRarities = defaultRarities.map((defaultRarity) => {
    const found = PackageRarities.find(
      (r) => r.rarity === defaultRarity.rarity
    );
    return found ? { ...defaultRarity, ...found } : defaultRarity;
  });

  return (
    <Card
      sx={{
        position: 'relative',
        maxWidth: 600,
        mx: 'auto',
        overflow: 'hidden',
        borderRadius: '16px',
        background:
          'linear-gradient(180deg, rgba(11, 17, 25, 0.92), rgba(7, 11, 17, 0.96))',
        border: `1px solid ${
          isTopTier ? 'rgba(56, 232, 255, 0.5)' : C.lineSoft
        }`,
        boxShadow: isTopTier
          ? '0 0 0 1px rgba(56, 232, 255, 0.2), 0 22px 60px -30px rgba(56, 232, 255, 0.55)'
          : '0 18px 40px -26px rgba(0, 0, 0, 0.9)',
        transition: 'transform 0.16s ease, border-color 0.16s ease',
        '&:hover': {
          transform: 'translateY(-3px)',
          borderColor: 'rgba(56, 232, 255, 0.45)'
        }
      }}
    >
      {isTopTier && (
        <Box
          sx={{
            position: 'absolute',
            top: 14,
            left: 14,
            zIndex: 2,
            px: 1.25,
            py: 0.5,
            borderRadius: '999px',
            background: 'rgba(255, 207, 64, 0.14)',
            border: '1px solid rgba(255, 207, 64, 0.5)',
            font: `800 10px ${C.mono}`,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: C.gold
          }}
        >
          Best value
        </Box>
      )}

      <CardMedia
        component="img"
        image={imageUrl}
        alt={name}
        sx={{
          height: 420,
          objectFit: 'contain',
          background: 'rgba(0, 0, 0, 0.25)',
          borderBottom: `1px solid ${C.lineSoft}`
        }}
      />

      <Box sx={{ p: 2.25 }}>
        {type === 'Subscription' && startingBonusEntries > 0 && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: '12px',
              textAlign: 'center',
              background: 'rgba(66, 230, 164, 0.1)',
              border: '1px solid rgba(66, 230, 164, 0.4)'
            }}
          >
            <Typography
              sx={{
                color: C.green,
                font: `900 13px ${C.mono}`,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                lineHeight: 1.4
              }}
            >
              Bonus: start with{' '}
              {startingBonusEntries.toLocaleString('en-AU')} entries
            </Typography>
          </Box>
        )}

        {type === 'Subscription' && discountedSubscriptionPrice && (
          <Box
            sx={{
              mb: 2,
              p: 1.75,
              borderRadius: '12px',
              textAlign: 'center',
              background: 'rgba(56, 232, 255, 0.08)',
              border: '1px solid rgba(56, 232, 255, 0.35)'
            }}
          >
            <Typography
              sx={{
                font: `800 10px ${C.mono}`,
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: C.cyan,
                mb: 0.75
              }}
            >
              30% off first month
            </Typography>
            <Typography
              sx={{
                font: `900 26px ${C.mono}`,
                color: C.text,
                fontVariantNumeric: 'tabular-nums'
              }}
            >
              A${discountedSubscriptionPrice}
              <Box
                component="span"
                sx={{
                  ml: 1,
                  font: `700 15px ${C.mono}`,
                  color: C.dimmer,
                  textDecoration: 'line-through'
                }}
              >
                A${price}
              </Box>
            </Typography>
            <Typography
              sx={{
                mt: 0.5,
                font: `600 12px ${C.mono}`,
                color: C.dim
              }}
            >
              Then A${price}/month after the first month
            </Typography>
          </Box>
        )}

        <Typography
          sx={{
            font: `800 10px ${C.mono}`,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: C.dim,
            mb: 1.25
          }}
        >
          Available rarities
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {mergedRarities.map((rarity, index) => {
            const col = rarityColor(rarity);
            return (
              <Chip
                key={index}
                label={`${rarity.rarity.charAt(0).toUpperCase()}${rarity.rarity.slice(1)}`}
                size="small"
                sx={{
                  height: 26,
                  color: col,
                  background: rarity.guaranteed
                    ? 'rgba(66, 230, 164, 0.08)'
                    : 'transparent',
                  border: `1px solid ${col}66`,
                  borderRadius: '8px',
                  '& .MuiChip-label': {
                    px: 1,
                    font: `700 11px ${C.mono}`,
                    letterSpacing: '0.03em'
                  }
                }}
              />
            );
          })}
        </Box>

        <Button
          variant="contained"
          fullWidth
          disableElevation
          onClick={() => handlePurchaseSelection(packageData)}
          disabled={disabled}
          data-track="package.purchase"
          data-track-name={name}
          data-track-type={type}
          data-track-price={String(price)}
          data-track-top={isTopTier ? '1' : '0'}
          sx={{
            mt: 2.25,
            py: 1.25,
            borderRadius: '10px',
            font: `800 13px ${C.mono}`,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            background: C.cyan,
            color: '#04222a',
            '&:hover': { background: '#63eeff' },
            '&.Mui-disabled': {
              background: 'rgba(140, 170, 190, 0.12)',
              color: C.dimmer
            }
          }}
        >
          {packageData.purchaseText ?? 'Purchase Now'}
        </Button>
      </Box>
    </Card>
  );
};
